/**
 * IXVerify v2 — capture flow state machine (ixv2 WP-3.2; plan §3.2/§4).
 *
 * Pure derivations from the server session detail + local capture state.
 * The SERVER owns the real state machine (WP-3.1 ixvSessions); this module
 * only decides what the wizard shows next and what the R12 loops
 * (retake / add-page) and R5 gates (amber fields need a human tap) demand.
 * No IO, no React — exhaustively unit-testable.
 */
import type {
  CaptureVerdict,
  ExtractedField,
  IxvAsset,
  IxvExtraction,
  IxvKit,
  KitState,
  SessionDetail,
  SessionStatus,
} from './api';

// ---------------------------------------------------------------------------
// Wizard steps
// ---------------------------------------------------------------------------

export type WizardStep =
  | 'start'
  | 'capture'
  | 'review'
  | 'attest'
  | 'published'
  | 'mismatch'
  | 'closed';

/**
 * Which wizard surface a session status lands on when it (re)opens —
 * "the top card on the dashboard" resume semantics. `wantsReview` lets the
 * user flip capture↔review locally without fighting the server status.
 */
export function stepForSession(detail: SessionDetail | null, local: { wantsReview?: boolean } = {}): WizardStep {
  if (!detail) return 'start';
  const status: SessionStatus = detail.session.status;
  switch (status) {
    case 'draft':
      return 'capture';
    case 'staged':
    case 'capturing':
      // Staged sessions open straight into kit scanning (§3.2: "open the
      // staged session → scan kits"); review is one tap away.
      return local.wantsReview ? 'review' : 'capture';
    case 'review':
      return local.wantsReview === false ? 'capture' : 'review';
    case 'completed':
    case 'second_check_pending':
    case 'verified':
      return 'published';
    case 'mismatch_recorded':
      return 'mismatch';
    case 'cancelled':
      return 'closed';
    default:
      return 'capture';
  }
}

// ---------------------------------------------------------------------------
// R12 — capture completeness loop
// ---------------------------------------------------------------------------

export interface CapturePrompt {
  kind: 'retake' | 'add_page';
  reasons: string[];
  /** e.g. "2 of 3" when a page label was seen. */
  pageLabelSeen: string | null;
}

/**
 * The retake / add-page prompt from the server's capture-completeness verdict
 * (§5.2 `page_capture_check` — photographed pages only). `null` = nothing to
 * prompt; the CRC keeps shooting.
 *
 * ADVISORY, not blocking (pilot r1): a recorded use-anyway override
 * (`capture.override`, stamped server-side) silences the prompt — the human
 * decision is on record and the attest gate honors it.
 */
export function capturePrompt(extraction: IxvExtraction | null | undefined): CapturePrompt | null {
  const capture = extraction?.capture;
  if (!capture) return null;
  const verdict: CaptureVerdict = capture.verdict;
  if (verdict === 'ok') return null;
  if (capture.override) return null;
  return {
    kind: verdict,
    reasons: capture.reasons || [],
    pageLabelSeen: (capture.check?.page_label_seen as string) ?? null,
  };
}

// ---------------------------------------------------------------------------
// Captured-page strip (pilot r1 — per-photo remove/retake)
// ---------------------------------------------------------------------------

export interface CapturePage {
  /** Stable render key (page index when known, else the asset id). */
  key: string;
  pageIndex: number | null;
  /** Server asset id a discard should target (original preferred). */
  discardAssetId: string | null;
  kind: 'photo' | 'pdf' | 'text';
  uploading: boolean;
  error: boolean;
}

/**
 * Merge the server's ACTIVE document assets with the local upload tasks into
 * the thumbnail-strip model. Pure: no IO, no React. Discarded assets never
 * appear; a page whose uploads are still in flight appears from the tasks
 * alone (discard disabled until a server id exists).
 */
export function derivePages(
  assets: ReadonlyArray<IxvAsset> | null | undefined,
  tasks: ReadonlyArray<{ pageIndex: number; tier: string; status: string; assetId: string | null }> | null | undefined
): CapturePage[] {
  const DOC_KINDS = ['ixrs_photo', 'ixrs_document', 'ixrs_text'];
  type Entry = {
    key: string;
    pageIndex: number | null;
    originalId: string | null;
    renditionId: string | null;
    kind: CapturePage['kind'];
    uploading: boolean;
    error: boolean;
    order: number;
  };
  const entries = new Map<string, Entry>();
  let seq = 0;
  const ensure = (key: string, pageIndex: number | null): Entry => {
    let e = entries.get(key);
    if (!e) {
      e = { key, pageIndex, originalId: null, renditionId: null, kind: 'photo', uploading: false, error: false, order: seq++ };
      entries.set(key, e);
    }
    return e;
  };

  for (const a of (assets || []).filter((x) => !x.discarded_at && DOC_KINDS.includes(x.kind))) {
    const key = a.page_index != null ? `p${a.page_index}` : `a${a.rendition_of || a.id}`;
    const e = ensure(key, a.page_index ?? null);
    if (a.tier === 'original') e.originalId = a.id;
    else e.renditionId = a.id;
    if (a.content_type === 'application/pdf') e.kind = 'pdf';
    else if (a.kind === 'ixrs_text') e.kind = 'text';
  }

  for (const t of tasks || []) {
    const e = ensure(`p${t.pageIndex}`, t.pageIndex);
    if (t.status === 'error') e.error = true;
    else if (t.status !== 'done') e.uploading = true;
    if (t.status === 'done' && t.assetId) {
      if (t.tier === 'original' && !e.originalId) e.originalId = t.assetId;
      if (t.tier === 'rendition' && !e.renditionId) e.renditionId = t.assetId;
    }
  }

  return [...entries.values()]
    .sort((a, b) => {
      if (a.pageIndex != null && b.pageIndex != null) return a.pageIndex - b.pageIndex;
      if (a.pageIndex != null) return -1;
      if (b.pageIndex != null) return 1;
      return a.order - b.order;
    })
    .map((e) => ({
      key: e.key,
      pageIndex: e.pageIndex,
      discardAssetId: e.originalId || e.renditionId,
      kind: e.kind,
      uploading: e.uploading,
      error: e.error,
    }));
}

// ---------------------------------------------------------------------------
// R5 — exception-based review gating
// ---------------------------------------------------------------------------

export const REVIEW_FIELDS = ['protocol_id', 'subject_number', 'visit_name', 'dispense_date'] as const;
export type ReviewFieldName = (typeof REVIEW_FIELDS)[number];

export interface FieldException {
  field: ReviewFieldName | 'kits';
  reasons: string[];
}

/** Every amber field/kit — the ONLY things the CRC must touch (§3.2 step 3). */
export function listExceptions(extraction: IxvExtraction | null | undefined): FieldException[] {
  if (!extraction) return [];
  const out: FieldException[] = [];
  for (const f of REVIEW_FIELDS) {
    const field: ExtractedField | undefined = extraction.fields?.[f];
    if (field && field.state === 'amber') out.push({ field: f, reasons: field.reasons || [] });
  }
  const kitReasons = (extraction.kits || [])
    .filter((k) => k.state === 'amber')
    .flatMap((k) => k.reasons || []);
  if (kitReasons.length > 0) out.push({ field: 'kits', reasons: Array.from(new Set(kitReasons)) });
  return out;
}

/** All green = a glance, nothing to tap per field. */
export function allGreen(extraction: IxvExtraction | null | undefined): boolean {
  return !!extraction && listExceptions(extraction).length === 0;
}

/**
 * The confirm CTA enables only when every amber exception carries an explicit
 * human decision — an edit or an accept tap (R5: AI proposes, humans confirm;
 * the confirm action itself is the recorded human confirmation for green
 * fields). `touched` holds the field names the CRC edited or accepted.
 */
export function canConfirm(extraction: IxvExtraction | null | undefined, touched: ReadonlySet<string>): boolean {
  if (!extraction) return false;
  return listExceptions(extraction).every((e) => touched.has(e.field));
}

/** The confirm payload prefilled from the AI proposal (finals; edits overlay). */
export function confirmPayloadFromExtraction(
  extraction: IxvExtraction,
  edits: Partial<Record<ReviewFieldName, string | null>> = {},
  kitEdits: string[] | null = null
): {
  fields: Record<ReviewFieldName, string | null>;
  kits: Array<{ kit_number: string; description?: string | null }>;
} {
  const fields = {} as Record<ReviewFieldName, string | null>;
  for (const f of REVIEW_FIELDS) {
    fields[f] = f in edits ? edits[f] ?? null : extraction.fields?.[f]?.value ?? null;
  }
  const kitNumbers =
    kitEdits ??
    (extraction.kits || [])
      .map((k) => k.kit_number?.value)
      .filter((v): v is string => v != null && String(v).trim() !== '');
  const byNumber = new Map((extraction.kits || []).map((k) => [k.kit_number?.value ?? '', k.description ?? null]));
  return {
    fields,
    kits: kitNumbers.map((n) => ({ kit_number: n, description: byNumber.get(n) ?? null })),
  };
}

// ---------------------------------------------------------------------------
// Kit progress + mismatch surface
// ---------------------------------------------------------------------------

export interface KitProgress {
  assigned: number;
  resolved: number;
  pending: number;
  discrepancies: number;
  pass: boolean;
  allResolved: boolean;
}

export function kitProgress(kitState: KitState | null | undefined): KitProgress {
  if (!kitState) {
    return { assigned: 0, resolved: 0, pending: 0, discrepancies: 0, pass: false, allResolved: false };
  }
  const c = kitState.counts;
  return {
    assigned: kitState.assigned_count,
    resolved: (c.matched || 0) + (c.manual_confirmed || 0),
    pending: c.pending || 0,
    discrepancies: (c.mismatch || 0) + (c.extra || 0) + (c.missing || 0),
    pass: kitState.pass,
    allResolved: kitState.all_resolved,
  };
}

/** Kit rows demanding a human decision (mismatch/extra + confusable readings). */
export function kitsNeedingDecision(kits: IxvKit[]): IxvKit[] {
  return kits.filter((k) => ['mismatch', 'extra'].includes(k.match_status));
}

// ---------------------------------------------------------------------------
// Attestation (D13 — one tap, three claims)
// ---------------------------------------------------------------------------

export interface AttestPlan {
  /** Certified-copy claim required (the document was photographed — R12). */
  needsCopyClaim: boolean;
  /** Originals still uploading — attestation blocks (R12). */
  originalsPending: boolean;
  /** Server-verified page completeness failed — back to the viewfinder. */
  captureIncomplete: boolean;
  subjectAttached: boolean;
  /** The attached roster row has no readable DOB (2026-09-04: CRIO had none
   *  on file, the epoch-zero placeholder was cleared) — the verbal DOB claim
   *  would be attested against nothing, so attest blocks until the CRC keys
   *  it from the source document (ReviewScreen "Enter date of birth"). */
  dobMissing: boolean;
  kitsPass: boolean;
  confirmed: boolean;
  ready: boolean;
}

export function attestPlan(detail: SessionDetail, localPendingOriginals = 0): AttestPlan {
  // Discarded pages are no longer evidence (pilot r1 soft discard) — they
  // neither demand the copy claim nor block on a pending original.
  const assets = (detail.assets || []).filter((a) => !a.discarded_at);
  const photographed = assets.some((a) => a.kind === 'ixrs_photo');
  const serverPendingOriginals = assets.filter(
    (a) => a.tier === 'rendition' && ['ixrs_document', 'ixrs_photo', 'ixrs_text'].includes(a.kind) && !a.rendition_of
  ).length;
  const capture = detail.session.extraction?.capture;
  const verdict = capture?.verdict;
  // A recorded use-anyway override clears the gate (server enforces the same).
  const captureIncomplete = photographed && verdict != null && verdict !== 'ok' && !capture?.override;
  const subjectAttached = !!detail.session.subject_id;
  const dobMissing = subjectAttached && !detail.subject?.dob;
  const kitsPass = !!detail.kit_state?.pass;
  // Server STATUS is the one truth (pilot r4): a demoted session (new page,
  // discard, or re-extract after a confirm) keeps its historical
  // assignment.confirmed_at, but the confirmation no longer covers the
  // evidence — the server will 409 an attest until it is re-confirmed. Only
  // status 'review' means "currently confirmed".
  const confirmed = detail.session.status === 'review';
  const originalsPending = serverPendingOriginals > 0 || localPendingOriginals > 0;
  return {
    needsCopyClaim: photographed,
    originalsPending,
    captureIncomplete,
    subjectAttached,
    dobMissing,
    kitsPass,
    confirmed,
    ready: confirmed && subjectAttached && !dobMissing && kitsPass && !originalsPending && !captureIncomplete,
  };
}
